// A network handed to ONE device is registered on its location too.
//
// ── The gap this closes ────────────────────────────────────────────────────
//
// The location's saved-network list is what makes adding Wi-Fi to a new device
// a single tap — SSID and password already known. But the list only grew from
// ONE place: the fleet dialog, which pushes a network to every device at a site.
// Onboarding, the device ⋮ menu, the desktop Wi-Fi tab and the console each
// sent to the device and stopped. So a network typed in for a single device —
// which is how most sites are set up, one device at a time — never entered the
// list, and the next device at the same site had it typed again.
//
// Now every single-device send calls this afterwards, and the list grows on its
// own. Measured before the change: Sanjay Ailani-Factory had 6 saved networks
// and Home 13, all from the fleet dialog or typed in by hand on the location
// screen.
//
// ⚠️ IDEMPOTENT BY OUR OWN CHECK, because the endpoint is not. There is no
// delete route for this list and `AddWifiNameAndWifiPasswordByLocationId` is not
// known to upsert, so registering the same SSID after every send would fill the
// list with duplicates that can never be removed. The current list is read
// first and an SSID already on it is left alone — and a password CHANGE for an
// existing SSID is NOT written, for the same reason: it would add a second row
// rather than replace the first, and the picker would then offer two passwords
// for one network. That case is rare and is the location screen's job.
//
// Best-effort and never throws. The device already has the network by the time
// this runs; a failure here leaves the list as it was, which is what it was.

import { getLocationWifiList } from '../api/api';
import { saveLocationWifi } from '../devices/wifiFleet';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../mqtt/mqttClient';

const norm = (s) => String(s ?? '').trim();
const rows = (res) => (Array.isArray(res && res.data) ? res.data : []);

/**
 * -> 'added' | 'exists' | 'skipped' | 'failed'
 *
 * 'skipped' means there was nothing to do: no location, or a blank SSID. The
 * firmware's own fallback networks (`isProtectedSsid`) are the caller's concern
 * — a device reports them but nobody should be provisioned onto them.
 */
export async function registerLocationWifi(locationId, ssid, password) {
  const id = Number(locationId);
  const name = norm(ssid);
  if (!(id > 0) || !name) return 'skipped';

  const existing = await getLocationWifiList(id).then(rows).catch(() => null);
  // A failed read is not licence to write: the one thing worse than a missing
  // entry is a duplicate that cannot be deleted.
  if (existing === null) return 'failed';

  const have = existing.some((r) => norm(r.WIFISSID ?? r.SSID ?? r.WifiName) === name);
  if (have) return 'exists';

  return (await saveLocationWifi(id, name, norm(password))) ? 'added' : 'failed';
}

/*
 * ── Register only once the device is SEEN ONLINE ───────────────────────────
 *
 * For a device being onboarded, "the send succeeded" proves less than it
 * sounds. Over BLE, `ok` means the firmware answered WIFI SAVED and rebooted —
 * it has STORED the credentials, not joined the network. A mistyped password is
 * saved just as happily. And the list has no delete route: register at that
 * moment and a wrong password sits in the picker for ever, offered to every
 * later device at the site.
 *
 * So onboarding does not register on save. It waits for the device's first
 * message on `<uno>/client` — the firmware publishes as soon as it is on the
 * network, and it cannot be on the network with a wrong password. That is the
 * only proof available that a network is "working", and it is exactly the one
 * the saved list should mean.
 *
 * Module-level, not screen-level: the watcher must outlive the onboarding
 * screen, because the user navigates away while the device is still rebooting.
 * Two minutes covers a reboot plus a slow join; after that the send is
 * assumed to have failed and nothing is written.
 */
const ONLINE_WINDOW_MS = 2 * 60 * 1000;
const pending = new Map(); // uno -> stop()

export function registerWhenDeviceOnline(uniqueNumber, locationId, ssid, password, {
  windowMs = ONLINE_WINDOW_MS,
} = {}) {
  const uno = norm(uniqueNumber);
  if (!uno || !(Number(locationId) > 0) || !norm(ssid)) return () => {};

  // A second provision of the same device replaces the first watch — the
  // credentials it would have registered are no longer the ones on the device.
  const prior = pending.get(uno);
  if (prior) prior();

  const topic = `${uno}/client`;
  let done = false;
  let timer = null;
  let off = () => {};

  const stop = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    off();
    unsubscribeTopic(topic);
    pending.delete(uno);
  };

  subscribeTopic(topic);
  off = addMessageListener((t) => {
    if (t !== topic || done) return;
    stop();
    // The device is on the air, so the network works. Fire and forget — the
    // screen that started this may be long gone.
    registerLocationWifi(locationId, ssid, password).catch(() => {});
  });
  timer = setTimeout(stop, windowMs);
  pending.set(uno, stop);
  return stop;
}

export default registerLocationWifi;
